import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  REVIEW_STATUS_COLORS,
  canUserMarkKhVerified,
  countOutstandingKhReaders,
  khEmailListFromFrontmatter,
  hasKhAcknowledgedCurrentVerify,
  reviewStatusLabel,
  type ReviewStatus,
} from '@/lib/knowledge-hub-utils'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { useAuth } from '@/providers/auth-provider'
import { toast } from 'sonner'
import { KnowledgeHubRequestReviewModal } from './knowledge-hub-request-review-modal'
import { useKhWorkspaceMembers } from './knowledge-hub-people-picker'

export type KnowledgeHubStatusStripProps = {
  documentId: string
  status: ReviewStatus
  verifiedBy?: string
  verifiedAt?: string
  frontmatter?: Record<string, unknown>
  /** Document workspace. Used to pick the member's linked address. */
  organizationId?: string
  onStatusChange?: () => void
}

const KH_ERROR_MESSAGES: Record<string, string> = {
  NOT_APPROVER: 'Only listed approvers can mark this document verified.',
  REVIEW_REQUIRED: 'This document must be in review before it can be verified.',
  NOT_VERIFIED_YET: 'This document has not been verified yet.',
  SELF_ADDED_APPROVER: 'Cannot verify: you recently added yourself to the approvers list.',
}

/** Toolbar status strip with review actions for Knowledge Hub-enabled documents. */
export function KnowledgeHubStatusStrip({
  documentId,
  status,
  verifiedBy,
  verifiedAt,
  frontmatter,
  organizationId,
  onStatusChange,
}: KnowledgeHubStatusStripProps) {
  const { user } = useAuth()
  const userEmail = user?.email
  const { members } = useKhWorkspaceMembers(organizationId)
  const self = members.find(
    (member) => member.email.trim().toLowerCase() === (userEmail || '').trim().toLowerCase()
  )
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [marking, setMarking] = useState(false)
  const [acknowledging, setAcknowledging] = useState(false)

  const colorClass = REVIEW_STATUS_COLORS[status] || 'bg-muted text-muted-foreground border-border'
  const fm = frontmatter ?? {}
  const outstandingReaders = countOutstandingKhReaders(fm)
  const readerEmails = khEmailListFromFrontmatter(fm, 'readers').map((email) =>
    email.trim().toLowerCase()
  )
  const hasReaders = readerEmails.length > 0
  const selfEmails = [userEmail, self?.notify_email]
    .map((email) => (email ? email.trim().toLowerCase() : ''))
    .filter(Boolean)
  const userIsReader = selfEmails.some((email) => readerEmails.includes(email))
  const showAcknowledge =
    status === 'active' &&
    Boolean(verifiedAt) &&
    userIsReader &&
    !selfEmails.some((email) => hasKhAcknowledgedCurrentVerify(fm, email))
  const showMarkVerified =
    canUserMarkKhVerified(fm, [userEmail, self?.notify_email]) &&
    (status === 'review-requested' || status === 'needs-review')

  const notifyUpdated = () => {
    onStatusChange?.()
    window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId } }))
  }

  const handleMarkVerified = async () => {
    setMarking(true)
    try {
      const { fetchWithAuth } = await import('@/lib/api')
      const res = await fetchWithAuth('/api/knowledge-hub/mark-verified', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_id: documentId,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const code = typeof data.code === 'string' ? data.code : ''
        const message =
          KH_ERROR_MESSAGES[code] ||
          (typeof data.error === 'string' ? data.error : 'Failed to mark verified')
        toast.error(message)
        return
      }
      notifyUpdated()
    } finally {
      setMarking(false)
    }
  }

  const handleAcknowledgeRead = async () => {
    setAcknowledging(true)
    try {
      const { fetchWithAuth } = await import('@/lib/api')
      const res = await fetchWithAuth('/api/knowledge-hub/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: documentId }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        const code = typeof data.code === 'string' ? data.code : ''
        const message =
          KH_ERROR_MESSAGES[code] ||
          (typeof data.error === 'string' ? data.error : 'Failed to record acknowledgement')
        toast.error(message)
        return
      }
      toast.success('Read receipt recorded', { duration: 2000, position: 'bottom-right' })
      notifyUpdated()
    } finally {
      setAcknowledging(false)
    }
  }

  return (
    <>
      <div
        className="flex h-11 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-3"
        data-testid={testIds.knowledgeHub.statusStrip}
      >
        <Badge className={cn('border text-xs capitalize', colorClass)}>
          {reviewStatusLabel(status)}
        </Badge>
        {verifiedBy ? (
          <span className="text-xs text-muted-foreground truncate">
            Verified by {verifiedBy}
            {verifiedAt ? ` (${verifiedAt.slice(0, 10)})` : ''}
          </span>
        ) : null}
        {hasReaders && outstandingReaders > 0 ? (
          <Badge
            variant="outline"
            className="text-xs shrink-0"
            data-testid={testIds.knowledgeHub.outstandingReadersBadge}
          >
            {outstandingReaders} not yet read
          </Badge>
        ) : null}
        <div className="flex-1" />
        {showAcknowledge ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => void handleAcknowledgeRead()}
            disabled={acknowledging}
            data-testid={testIds.knowledgeHub.acknowledgeReadButton}
          >
            {acknowledging ? 'Saving...' : "I've read this"}
          </Button>
        ) : null}
        {status !== 'review-requested' ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowRequestModal(true)}
            data-testid={testIds.knowledgeHub.requestReviewButton}
          >
            Request review
          </Button>
        ) : null}
        {showMarkVerified ? (
          <Button
            variant="default"
            size="sm"
            className="h-7 text-xs"
            onClick={() => void handleMarkVerified()}
            disabled={marking}
            data-testid={testIds.knowledgeHub.markVerifiedButton}
          >
            {marking ? 'Saving...' : 'Mark verified'}
          </Button>
        ) : null}
      </div>
      <KnowledgeHubRequestReviewModal
        open={showRequestModal}
        onOpenChange={setShowRequestModal}
        documentId={documentId}
        onSuccess={() => {
          notifyUpdated()
        }}
      />
    </>
  )
}
